/**
 * Speaking-score service — EAA-1754/1755.
 *
 * Powers "interactive-reading" pronunciation grading. Starts an ASYNC aihub job
 * via the ACH backend `POST /content/speaking-score/generate` (returns
 * `202 {job_id}`) and polls `GET /integrations/jobs/{job_id}` until it completes.
 * The worker forwards the reference script (`text`) + the learner recording
 * (`user_audio_file`) to the aihub AI agent (`/api/v3/scoring/speaking/speechace`),
 * which returns an overall score, pronunciation, and per-word feedback.
 *
 * `contentId` (when known) lets the backend resolve the tenant from the content —
 * so staff/superadmin preview needs no tenant id — and lets a launched learner
 * session authenticate with its session JWT (`Authorization: Session <token>`).
 * All AI calls run inside AI Hub as a job; scoring credentials stay server-side.
 */

import { isPersistedContentId } from '@ai-content-hub/content-core';
import { sharedApiClient, getApiConfig, type PostFormDataOptions } from './client';
import { generateService } from './generate.service';

/** Parameters for a pronunciation-scoring request. */
export interface SpeakingScoreParams {
    /** Reference script the learner was asked to read. */
    text: string;
    /** The learner's recording (Blob/File from MediaRecorder). */
    audio: Blob | File;
    /** 1 when `text` spans multiple lines (agent contract); defaults to 0. */
    noMc?: number;
    /** Optional request id echoed back by the agent. */
    questionInfo?: string;
    /** Saved content id — lets the backend resolve tenant + validate a session. Non-UUID ids are dropped. */
    contentId?: string;
    /** Host tenant id (fallback when there's no content id, e.g. staff preview). */
    tenantId?: string;
}

/** Raw scoring result from the agent (via the aihub job result). */
export interface SpeakingScoreResult {
    /** Overall score (scale depends on engine; ELSA 0-5, Speechace 0-100). */
    score?: number;
    fluency?: number | null;
    pronunciation?: number | null;
    question_info?: string;
    /** Raw gateway body — carries per-word feedback (word_score_list, etc.). */
    details?: Record<string, unknown>;
}

function buildScoreFormData(params: SpeakingScoreParams): FormData {
    const form = new FormData();
    form.append('text', (params.text ?? '').trim());
    form.append('audio', params.audio, 'recording.webm');
    form.append('no_mc', String(params.noMc ?? 0));
    if (params.questionInfo) form.append('question_info', params.questionInfo);
    // EAA-2117: an unsaved draft (question-bank `/new` preview) carries a client id the BE
    // cannot resolve, so only a saved content UUID is sent; the tenant then comes from
    // `tenant_id`, which callers fill from GradingContext.tenantId when the host knows it.
    if (isPersistedContentId(params.contentId)) form.append('content_id', params.contentId!);
    if (params.tenantId) form.append('tenant_id', params.tenantId);
    return form;
}

class SpeakingScoreService {
    private readonly basePath = '/content/speaking-score';

    /**
     * Grade a learner recording against the reference script. Starts the aihub
     * job, polls until it finishes, and resolves with the agent result.
     *
     * @param sessionToken - session JWT for a launched learner session; when
     *   provided, the start request and the poll use `Authorization: Session`.
     */
    async score(
        params: SpeakingScoreParams,
        sessionToken?: string,
        options?: PostFormDataOptions
    ): Promise<SpeakingScoreResult> {
        const form = buildScoreFormData(params);
        const path = `${this.basePath}/generate`;

        let jobId: string;
        if (sessionToken) {
            // WHY: in multi-player mode the global client carries a view-token for
            // content reads; scoring needs SessionTokenAuthentication. Use a raw
            // multipart fetch so the browser sets the boundary and we control auth.
            const config = getApiConfig();
            const res = await fetch(`${config.baseUrl}${path}`, {
                method: 'POST',
                headers: { Authorization: `Session ${sessionToken}` },
                body: form,
            });
            const json = (await res.json().catch(() => null)) as {
                data?: { job_id: string; status: string };
                message?: string;
            } | null;
            if (!res.ok) {
                throw new Error(json?.message || `speaking score failed (${res.status})`);
            }
            jobId = (json?.data ?? (json as unknown as { job_id: string })).job_id;
        } else {
            const startResponse = await sharedApiClient.postFormData<{
                job_id: string;
                status: string;
            }>(path, form, options);
            jobId = startResponse.data.job_id;
        }

        // Poll the aihub job. Scoring is typically a few seconds; allow
        // 2s × 90 = 180s, and fail fast if the worker never picks it up.
        const finalJob = await generateService.pollJobStatus(jobId, () => undefined, {
            interval: 2000,
            maxAttempts: 90,
            pendingTimeoutMs: 30_000,
            sessionToken,
        });

        if (finalJob.status === 'failed') {
            throw new Error(finalJob.error || 'Speaking scoring failed');
        }

        return (finalJob.result ?? {}) as unknown as SpeakingScoreResult;
    }
}

export const speakingScoreService = new SpeakingScoreService();
